import { RoutePaths } from './constants';
import { LocalStorageKey } from './constants/localStorageKey';
import { ExclusionListDate } from './global-state/types';
import { useLocalStorage } from './hooks';
import { AreaWidgetType } from './pages/areaOverview/util';
import { WidgetType } from './pages/home/types';
import { homeTranslations } from './translations';
import { areaOverviewTranslations } from './translations/areaOverview';
import { UserPermissions } from './utils';

export const isTodayInRange = (dateRanges: { startDate: string; endDate: string }[]) => {
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);

  // Convert date strings to Date objects
  const validRanges = dateRanges
    .map(({ startDate, endDate }) => ({
      startDate: new Date(startDate),
      endDate: new Date(endDate),
    }))
    .filter(({ startDate, endDate }) => {
      startDate.setUTCHours(0, 0, 0, 0);
      endDate.setUTCHours(23, 59, 59, 999);

      // Case 1: Today is within the start-end range
      const isInRange = today >= startDate && today <= endDate;

      // Case 2: Today is within 2 weeks before the start date
      const twoWeeksBefore = new Date(startDate);
      twoWeeksBefore.setDate(startDate.getDate() - 14);
      const isBeforeStart = today >= twoWeeksBefore && today < startDate;

      return isInRange || isBeforeStart;
    })
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime()); // Sort by nearest startDate

  // Get the closest valid range
  const matchingRange = validRanges.length > 0 ? validRanges[0] : null;

  return matchingRange
    ? {
        isInRange: true,
        startDate: matchingRange.startDate.toISOString().split('T')[0],
        endDate: matchingRange.endDate.toISOString().split('T')[0],
      }
    : { isInRange: false, startDate: '', endDate: '' };
};

export const getCanAccessPath = (
  routePath: RoutePaths,
  userPathPermissions: UserPermissions,
  isNonSignIn: boolean,
  exclusionList: ExclusionListDate[],
  brandId?: number,
): boolean => {
  const { getItem } = useLocalStorage();
  const showExclusionList = isTodayInRange(exclusionList);
  // Check if user is a area personel, if yes then we don't show area specific pages
  const selectedView = getItem(LocalStorageKey.SelectedView);
  const isArea = selectedView === 'area';
  const isHMbrand = brandId === 0;

  const permissionMap: { [key in RoutePaths]: boolean } = {
    [RoutePaths.HOME]: true,
    [RoutePaths.WIDGETS]: !isNonSignIn && !isArea,
    [RoutePaths.AREARESULT]: !isNonSignIn && !isHMbrand && userPathPermissions.areaResultPage.canRead,
    [RoutePaths.STORESLIST]: !isNonSignIn && isArea,
    [RoutePaths.EMPLOYEES]: !isNonSignIn && userPathPermissions.employeesAndRolesPage.canRead && !isArea,
    [RoutePaths.TRANSACTION]: !isNonSignIn && userPathPermissions.transactionSearchPage.canRead && !isArea,
    [RoutePaths.YEARLYSTOCKTAKE]:
      showExclusionList.isInRange && userPathPermissions.storeInventoryCount.canRead && !isArea,
    [RoutePaths.KPITARGET]: !isNonSignIn && userPathPermissions.kpiTargetsPage.canRead && !isArea,
    [RoutePaths.SOLDPRODUCTS]: !isNonSignIn && userPathPermissions.soldProductsPage.canRead && !isArea,
    [RoutePaths.PRODUCTCATEGORY]: !isNonSignIn && userPathPermissions.productIndexSubindex.canRead && !isArea,
    [RoutePaths.PROFITANDLOSS]: !isNonSignIn && userPathPermissions.profitAndLossPage.canRead && !isArea,
    [RoutePaths.WIDGETACCESSCONTROL]: !isNonSignIn && userPathPermissions.blacklistPermissionsForArea.canRead,
    [RoutePaths.SALESANALYSIS]: !isNonSignIn && userPathPermissions.salesAnalysisPage.canRead && isArea,
    [RoutePaths.ACCESSREQUESTS]: !isNonSignIn,
    [RoutePaths.PROFILE]: !isNonSignIn,
    [RoutePaths.DEVELOPMENT]: !isNonSignIn && process.env.NODE_ENV === 'development',
  };

  return permissionMap[routePath] ?? false;
};

// Overview Widgets Permissions list
export const overviewWidgetsPermissionsArray: (keyof UserPermissions)[] = [
  'conversionRateOverview',
  'membersOverview',
  'salesOverview',
  'efficiencyOverview',
  'queueAndCustomerOverview',
  'budgetAndPlanOverview',
  'kpiTargetsOverview',
  'rfidOverview',
  'getCountsOverview',
];

export const getWidgetTranslationData = (
  widgetType: WidgetType | AreaWidgetType,
): { key: string; defaultValue: string } | null => {
  switch (widgetType) {
    //  Sales and Efficiency
    case WidgetType.ConversionRate:
      return homeTranslations.conversionRate.title;
    case WidgetType.HourlyTurnover:
      return homeTranslations.hourlyTurnover.title;
    case WidgetType.PiecesPerReceipt:
      return homeTranslations.piecesPerReceipt.title;
    case WidgetType.AveragePurchaseWidget:
      return homeTranslations.averagePurchase.title;
    case WidgetType.EfficiencyWidget:
      return homeTranslations.efficiencyWidget.title;
    case WidgetType.SalesInsightsWidget:
      return homeTranslations.salesInsightWidget.title;

    //  Customer Base Value
    case WidgetType.NewMembers:
      return homeTranslations.newMembers.title;
    case WidgetType.TopSellingGarment:
      return homeTranslations.topSelling.title;
    case WidgetType.ShareOfReceipts:
      return homeTranslations.shareOfReceipts.title;
    case WidgetType.SCOShareOfReceipts:
      return homeTranslations.scoShareOfReceipts.title;
    case WidgetType.AppCustomersWidget:
      return homeTranslations.appCustomers.title;

    //  Customer Experience
    case WidgetType.QueueLengthWidget:
      return homeTranslations.queueLength.title;
    case WidgetType.CustomerFeedBackWidget:
      return homeTranslations.customerFeedback.title;
    case WidgetType.RfidApprovalsWidget:
      return homeTranslations.rfidApprovals.title;
    case WidgetType.RfidSalesRetagging:
      return homeTranslations.rfidSalesRetagging.title;
    case WidgetType.RfidCountWidget:
      return homeTranslations.rfidCount.title;
    case WidgetType.CustomerEngagmentWidget:
      return homeTranslations.customerEngagement.title;
    case WidgetType.OrisCustomersWidget:
      return homeTranslations.orisCustomers.title;
    case WidgetType.HelpASaleWidget:
      return homeTranslations.helpASale.title;
    case WidgetType.StockAccuracyWidget:
      return homeTranslations.stockAccuracy.title;
    case WidgetType.BopisWidget:
      return homeTranslations.bopis.title;

    // Area Overview Widgets
    case AreaWidgetType.SalesOverview:
      return areaOverviewTranslations.salesOverview.title;
    case AreaWidgetType.MembersOverview:
      return areaOverviewTranslations.memberOverview.title;
    case AreaWidgetType.ConversionRateOverview:
      return areaOverviewTranslations.conversionRateOverview.title;
    case AreaWidgetType.QueueCustomerOverview:
      return areaOverviewTranslations.queueCustomerOverview.title;
    case AreaWidgetType.EfficiencyOverview:
      return areaOverviewTranslations.efficiencyOverview.title;
    case AreaWidgetType.RfidOverview:
      return areaOverviewTranslations.rfidOverview.title;
    case AreaWidgetType.BudgetPlanOverview:
      return areaOverviewTranslations.budgetPlanOverview.title;
    case AreaWidgetType.KPIForecastOverview:
      return areaOverviewTranslations.kpiOverview.title;

    default:
      return null;
  }
};
